import { doc, onSnapshot, type Unsubscribe } from 'firebase/firestore';
import { useEffect, useState } from 'react';
import type { DistrictMapPmtiles, MapDownloadPolicy } from '@citizen-walk/shared-types';
import { db } from '../../lib/firebase';

/**
 * 単一地区の `mapPmtiles` メタデータ subscribe hook (Phase 5.3 R-33 step 4)。
 *
 * MapDownloadPage / MapPage が「admin が最新版を生成したら自動反映」できるよう
 * onSnapshot で realtime listen。`districts/{id}.mapPmtiles` 部分のみ取り出して返す。
 *
 * 状態:
 *   - 'loading': 初回取得待ち
 *   - 'ready': mapPmtiles あり (DistrictMapPmtiles を return)
 *   - 'absent': district doc は存在するが mapPmtiles 未生成
 *   - 'not-found': district doc 不在
 *   - 'error': read エラー (rules 違反 / network)
 */

export type DistrictPmtilesState =
  | { status: 'loading'; meta: null; policy: null; error: null }
  | {
      status: 'ready';
      meta: DistrictMapPmtiles;
      policy: MapDownloadPolicy;
      error: null;
    }
  | { status: 'absent'; meta: null; policy: MapDownloadPolicy; error: null }
  | { status: 'not-found'; meta: null; policy: null; error: null }
  | { status: 'error'; meta: null; policy: null; error: Error };

export function useDistrictPmtiles(districtId: string | null): DistrictPmtilesState {
  const [state, setState] = useState<DistrictPmtilesState>({
    status: 'loading',
    meta: null,
    policy: null,
    error: null,
  });
  useEffect(() => {
    if (!districtId) {
      setState({ status: 'not-found', meta: null, policy: null, error: null });
      return;
    }
    const ref = doc(db, 'districts', districtId);
    const unsub: Unsubscribe = onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) {
          setState({ status: 'not-found', meta: null, policy: null, error: null });
          return;
        }
        const data = snap.data();
        const meta = (data?.mapPmtiles ?? null) as DistrictMapPmtiles | null;
        // Sprint BG-DataSettings 19 (U-C13): mapDownloadPolicy も同 doc snapshot から抽出。
        // default は 'manual' (旧 v1 / docs/04 §1-1 と一致)。AutoMapDialog が 'auto' のみ反応。
        const policy = ((data?.mapDownloadPolicy as MapDownloadPolicy | undefined) ??
          'manual') as MapDownloadPolicy;
        if (!meta) {
          setState({ status: 'absent', meta: null, policy, error: null });
          return;
        }
        setState({ status: 'ready', meta, policy, error: null });
      },
      (error) => setState({ status: 'error', meta: null, policy: null, error }),
    );
    return () => unsub();
  }, [districtId]);
  return state;
}
